/**
 * Needs-Attention Tray Contracts
 *
 * Shared types for the "Requieren atención" tray (issue #322, B.2).
 *
 * Backend serves these endpoints; frontend consumes them.
 * Both sides depend on this file as the single source of truth.
 *
 * A transaction lands in the tray when:
 *   - It was promoted to `bat.transactions` by the auto-promoter, AND
 *   - Its `bat.imported_transactions` row has `validationStatus = 'warning'`.
 *
 * The user resolves the warning in one of two ways:
 *   1. `map_asset` — register `from` ticker → `toAssetId` in the tenant alias
 *      catalogue and propagate the resolution to all matching ledger rows.
 *   2. `discard`   — soft-delete the ledger row and detach the staging row.
 */

// ============================================================================
// Warning type discriminator
// ============================================================================

/**
 * Distinct warning categories that surface in the tray.
 *
 * Vocabulary aligned with issue #322 B.2.2 (the import-pipeline spec).
 * Each member maps 1:1 to a resolution handler on the frontend:
 *
 *   - `unknown_asset_alias` — raw ticker has no resolved assetId; resolver
 *     maps the ticker to a canonical asset (or discards the row).
 *   - `suspicious_date`     — timestamp is outside the plausible range
 *     (e.g. `> now()` or before BTC genesis, 2009-01-01). Resolver: edit
 *     date or discard.
 *     ⚠️⚠️ NOT the home for a date the import could not READ (→ `unreadable_date`).
 *     A suspicious date PARSED and is implausible; an unreadable one never
 *     parsed, has no ledger row to edit or discard, and its remedy is the
 *     template's date format. This member is also RESERVED, so filing rows here
 *     would certify a check that does not run — the exact lie
 *     {@link RESERVED_ATTENTION_WARNING_TYPES} prevents.
 *   - `suspicious_price`    — price is implausible vs. historical market
 *     data. Resolver: confirm as correct or discard.
 *   - `near_duplicate`      — partial-fingerprint match with another
 *     transaction (does not collide on `import_hash`). Resolver: confirm
 *     as unique or discard.
 *   - `missing_counterparty`— transfer with no identifiable wallet on the
 *     other side. Resolver: tag counterparty or discard.
 *   - `unknown_operation`   — a row the V2 rule pipeline could not classify
 *     (attribute='__unknown__'); persisted to staging with
 *     validationStatus='unknown' and linkedTransactionId NULL. Resolver:
 *     reprocess with current template rules.
 *   - `unreadable_date`     — the SAME staging state as `unknown_operation`
 *     (unclassified, no ledger row, same "reprocess" resolver) with the reason
 *     NAMED: the date cell matched neither the template's `dateFormat` nor the
 *     loose fallback. Split out because that badge is FALSE for it — step 4.8
 *     resolves the attribute BEFORE looking at the date, so the tray read
 *     «Operación desconocida» two cells from the operation it had parsed — and
 *     because the category is the FILTERABLE field. ⚠️ NOT `suspicious_date`.
 *   - `invalid_transaction_shape` — the ledger writer refused the row on the
 *     transaction CREATE rules (`validation_status='error'`, no ledger row).
 *     Resolver: fix the template's column mapping, or discard.
 *   - `side_contradicts_legs` — the row DECLARES one side ("Venta") and the
 *     legs the mapping gave it say the other. Resolver: swap the totals in the
 *     template's parameter rules, or discard.
 *
 * PRODUCER STATUS — see {@link RESERVED_ATTENTION_WARNING_TYPES} for the
 * authoritative list of the members nothing produces, and WHY each one is
 * empty. Members with a live producer today:
 *   - `unknown_asset_alias` — produced by `validation-status-deriver.ts`
 *     and surfaced by `needs-attention.service.ts`.
 *   - `unknown_operation`   — produced by `ImportTransactionCreator`
 *     persisting unknown rows (validationStatus='unknown',
 *     linkedTransactionId NULL) and surfaced by `needs-attention.service.ts`.
 *   - `unreadable_date`     — SAME writer; split off that set by the
 *     `Invalid date` token already in `validation_errors`.
 *   - `invalid_transaction_shape` — produced by `ledger-write-rule-guard.ts`
 *     stamping `validation_status='error'` on a refused row.
 *   - `side_contradicts_legs` — produced by `ImportTransactionCreator` and
 *     `custom-import-staging-writer.service.ts` persisting the engine's
 *     side-contradiction token into `validation_errors`.
 */
/**
 * A VALUE, not just a type: the backend's `?type=` query enum is built from this
 * array (`needs-attention.validators.ts`). It used to be a hand-written zod
 * enum "kept in lock-step" by comment alone — and it drifted the moment a new
 * member arrived: the tray offered the new badge in its Tipo dropdown and the
 * API answered `Invalid query parameters`. Browser-verified.
 */
export const ATTENTION_WARNING_TYPES = [
  'unknown_asset_alias',
  /**
   * A leg that went through a catalogued bridge (Across, Stargate, CCTP, a
   * canonical L1/L2 bridge, Hyperliquid's Bridge2) whose other leg is not
   * joined yet: IN TRANSIT, never a sale or a gift. It leaves the tray when
   * the matcher (or the person) joins it, or when it is classified.
   */
  'bridge_in_transit',
  'suspicious_date',
  'suspicious_price',
  'near_duplicate',
  'missing_counterparty',
  'unknown_operation',
  'unreadable_date',
  /**
   * El GEMELO de `unreadable_date` para el IMPORTE: la celda contradice el
   * formato de números declarado («0.399» en un fichero EU), es ilegible o está
   * fuera de rango. La operación SÍ se leyó, así que «Operación desconocida» es
   * falso; `warningDetails.rawAmount` lleva la celda tal como vino.
   */
  'unreadable_amount',
  'invalid_transaction_shape',
  /**
   * ⚠️⚠️ La fila declara «Venta» y sus patas mapeadas dicen COMPRA (o al revés).
   *
   * El motor V2 lo detecta desde el paso 4.125 y la PREVIA lo pinta bajo
   * «Motivo», pero el aviso moría en la escritura: la fila entraba al libro
   * indistinguible de una compra legítima, así que una enajenación tributable
   * se convertía en un lote de coste que no existe y NADA quedaba diciendo que
   * el propio importador dudaba.
   *
   * ⚖️ NO bloquea la escritura — hay ficheros cuya columna de cantidad significa
   * «lo que recibes», y ahí la colocación es la correcta. La fila SE IMPORTA y
   * además se marca. El remedio es del usuario: «Intercambiar Total compra y
   * venta» en las reglas de parámetro de su plantilla, o descartar el aviso.
   */
  'side_contradicts_legs',
  /**
   * An EVM method-signature transaction moves more than one asset on the SAME
   * side for the importing wallet (`addLiquidityETH`: ETH and a token leave; a
   * tax-on-transfer token emitting a second `Transfer`). One ledger row holds
   * one leg per side, so classifying it with the first leg would invent a
   * disposal or lose one. The classifier leaves the row WITHOUT an attribute
   * (`transactionAttribute: null`), so it never reaches the ledger or the tax
   * calculation until someone reviews it. RESERVED: no tray writer stores it yet.
   */
  'multiple_legs_on_one_side',
] as const

export type AttentionWarningType = (typeof ATTENTION_WARNING_TYPES)[number]

/**
 * The members of {@link ATTENTION_WARNING_TYPES} that NOTHING can produce today,
 * so filtering the tray by one of them returns an empty page that says nothing
 * whatsoever about the customer's data.
 *
 * ⚠️⚠️ WHY THIS IS A SHARED VALUE AND NOT A LIST IN THE TRAY. The tray renders
 * two OPPOSITE conclusions over the same empty result:
 *
 *   reserved → «Esta comprobación todavía no está disponible … el resultado
 *              vacío NO significa que tus datos estén limpios»
 *   live     → «Ninguna transacción coincide con estos filtros»
 *
 * In a tax product that is the difference between "we did not look" and "we
 * looked and found nothing", so the two layers MUST agree. They did not: this
 * list lived hand-written in `packages/frontend/.../reserved-warning-types.ts`
 * with four members while the backend reserved three, and nothing compared them.
 * That is the same drift `ATTENTION_WARNING_TYPES` above was hoisted here to
 * kill — a list "kept in lock-step" by comment alone always drifts.
 *
 * WHY EACH MEMBER IS EMPTY — the two reasons are NOT the same, and conflating
 * them is what let the divergence live:
 *
 *   · `suspicious_date`, `suspicious_price`, `missing_counterparty` own NO
 *     classification rule at all, so `buildWarningTypeSqlPredicate` (backend)
 *     returns `null` for them and the endpoint answers an honest empty page.
 *
 *   · `near_duplicate` DOES own a rule (it matches
 *     `imported_transactions.duplicate_status = 'near_duplicate'`), but NO
 *     writer ever stores that value. Every write of the column sets `'unique'`
 *     (`ImportTransactionCreator`, `custom-import-staging-writer.service`) or
 *     `'duplicate'` (`ImportTransactionRepository`, `ledger-writer.service`);
 *     the `CHECK` constraint admits the third value and no migration or seed
 *     produces it. The rule is reachable code over an unreachable value.
 *
 * Both halves are enforced by
 * `backend/.../__tests__/needs-attention-reserved-types.parity.unit.test.ts`,
 * which fails if this list and the backend's real behaviour ever disagree in
 * EITHER direction. Do not edit this list without reading that test's failure
 * message: dropping a member promises a check that is not run, and adding one
 * denies a check that is.
 */
export const RESERVED_ATTENTION_WARNING_TYPES = [
  'suspicious_date',
  'suspicious_price',
  'missing_counterparty',
  'near_duplicate',
  'multiple_legs_on_one_side',
] as const satisfies readonly AttentionWarningType[]

export type ReservedAttentionWarningType = (typeof RESERVED_ATTENTION_WARNING_TYPES)[number]

/**
 * The types that describe ONE staging state: a row the V2 pipeline left
 * UNCLASSIFIED (`validation_status='unknown'`, `linked_transaction_id` NULL, no
 * ledger row). They differ only in how much the engine could SAY about why.
 * ⚠️⚠️ WHY THIS IS A SHARED VALUE. FIVE doors asked the question separately: the
 * "Reprocesar" button, the raw operation-type cell and the cancelled badge
 * (`NeedsAttentionTrayRow`), the reason sub-line (`needs-attention-reason.ts`)
 * and the backend's warning-details builder (`needs-attention.service.ts`).
 * Splitting `unreadable_date` off `unknown_operation` moved rows, not state, so
 * all five must keep opening or the split trades a wrong label for dead
 * affordances. Written ONCE so a future split cannot leave one mute. NOT a
 * member: `invalid_transaction_shape` — classified, refused on its SHAPE.
 */
export const UNCLASSIFIED_ROW_WARNING_TYPES = [
  'unknown_operation',
  'unreadable_date',
  'unreadable_amount',
] as const satisfies readonly AttentionWarningType[]

/** True for a warning type in {@link UNCLASSIFIED_ROW_WARNING_TYPES}. */
export function isUnclassifiedRowWarningType(warningType: AttentionWarningType): boolean {
  return (UNCLASSIFIED_ROW_WARNING_TYPES as readonly string[]).includes(warningType)
}
// ============================================================================
// Ledger CREATE-rule refusal
// ============================================================================

/**
 * WHY the ledger writer refused an imported row: the attribute does not permit
 * the legs the row carries. Locale-free codes — the backend emits the code, the
 * tray renders `needsAttention.reasons.<code>` from the `imports` catalog.
 *
 * These are the SAME rule codes `POST /api/transactions` answers 400 with
 * (`TransactionCreateRuleCode` in
 * `core/transactions/domain/services/create-rule-violations.ts`), lowercased for
 * the i18n key. They are carried per row because each has a different remedy:
 *   - `missing_incoming_amount` / `missing_outgoing_amount` — the attribute
 *     requires that leg and the file did not map a column to it. Fix the
 *     template's column mapping, or change the operation's mapped attribute.
 *   - `incoming_leg_not_allowed` / `outgoing_leg_not_allowed` — the attribute's
 *     direction forbids that leg (a deposit that also spends something invents a
 *     disposal in Balances). Usually the mapped attribute is wrong for the row.
 *   - `commission_not_allowed` — the attribute declares `allows_commission =
 *     false`; the mapped fee column belongs to a different operation.
 *   - `no_amounts` — no leg carried a value at all; no balance, match or tax
 *     calculation could ever act on the row.
 *
 * NOT members: `INVALID_ATTRIBUTE`, `FUTURE_DATE` and `NEGATIVE_AMOUNT`. The
 * writer deliberately does not block on those — see the evidence recorded in
 * `imports/domain/services/ledger-write-rule-guard.ts`. Naming them here would
 * promise the tray a label nothing can ever produce.
 */
export const ATTENTION_LEDGER_RULE_CODES = [
  'missing_incoming_amount',
  'missing_outgoing_amount',
  // Importe sin su activo: la regla vivía sólo en el esquema Zod de la API, así
  // que la previa daba «✓ ok» a una fila que nunca entraba (medido 2026-08-15).
  'missing_incoming_asset',
  'missing_outgoing_asset',
  'incoming_leg_not_allowed',
  'outgoing_leg_not_allowed',
  'commission_not_allowed',
  'no_amounts',
] as const

export type AttentionLedgerRuleCode = (typeof ATTENTION_LEDGER_RULE_CODES)[number]

// ============================================================================
// Unknown-row rejection reason
// ============================================================================

/**
 * WHY the V2 rule pipeline refused to classify a row that surfaces as
 * `unknown_operation`. Locale-free codes: the backend emits the code, the tray
 * renders `needsAttention.reasons.<code>` from the `imports` catalog.
 *
 * Each code has a DIFFERENT remedy, which is the whole point of carrying it —
 * before it existed every unclassified row read "Operación desconocida":
 *   - `invalid_amount`      — the mapped amount cell was not a number. Fix the file
 *                             or the column mapping.
 *   - `invalid_date`        — the mapped date cell matched neither the template's
 *                             `dateFormat` nor the loose fallback. Fix the
 *                             template's date format, then reprocess.
 *   - `amount_out_of_range` — the amount exceeds the staging column's
 *                             `numeric(20,8)` range (12 integer digits).
 *   - `amount_format_eu` / `amount_format_us` — the cell is unambiguously written
 *                             in the OPPOSITE number convention to the one the
 *                             template declares. Fix the template's number format.
 *
 * NOT a member: "the operation type is unmapped". The engine computes NO reason
 * for that row, and `warningDetails.reason` is `null` for it — see
 * `unknown-row-reason.ts` (backend). Naming it here would invite defaulting to
 * it, which would put a confident wrong answer where the truth is "unstated".
 *
 * Distinct from `StillUnknownReason` (unknown-rows-reprocess.ts), which reports
 * the OUTCOME of a reprocess attempt, not the classifier's original refusal.
 */
export const ATTENTION_UNKNOWN_ROW_REASON_CODES = [
  'invalid_amount',
  'invalid_date',
  'amount_out_of_range',
  'amount_format_eu',
  'amount_format_us',
  // Los dos que faltaban, y que el catálogo del backend nombraba en su lista de
  // «deliberadamente no cubiertos» con la condición exacta para añadirlos:
  // «exige que el MOTOR declare el motivo primero». Lo declara desde #1626.
  'operation_type_not_mapped',
  'operation_type_column_not_mapped',
] as const

export type AttentionUnknownRowReasonCode = (typeof ATTENTION_UNKNOWN_ROW_REASON_CODES)[number]

// ============================================================================
// Row shape
// ============================================================================

/**
 * A single row in the needs-attention tray.
 *
 * `id`            = `imported_transactions.id`     (staging row)
 * `transactionId` = `transactions.id`              (ledger row)
 *
 * The two IDs are joined via `imported_transactions.linked_transaction_id`.
 */
export interface AttentionRow {
  /** Staging row ID — primary key in `imported_transactions` */
  id: string

  /**
   * Ledger row ID — primary key in `transactions` (already promoted).
   * NULL for `unknown_operation` rows: those have no ledger transaction
   * (validationStatus='unknown', linkedTransactionId NULL) until reprocessed.
   */
  transactionId: string | null

  /** Discriminator the frontend uses to pick the right resolver UI */
  warningType: AttentionWarningType

  /**
   * Type-specific payload. For `unknown_asset_alias` this looks like
   * `{ ticker: 'BCC', count: 5 }` where `count` is the total number of
   * transactions in the tray that share the same raw ticker. The frontend
   * uses `count` to render the "applies to N rows" hint.
   *
   * For `unknown_operation` it is
   * `{ operationType, cancelled, reason }` where `reason` is an
   * {@link AttentionUnknownRowReasonCode} or `null` when the classifier
   * computed no reason (the unmapped-operation-type row).
   */
  warningDetails: Record<string, unknown>

  /**
   * For an `unknown_asset_alias` row: the ONE catalogue asset whose symbol is
   * the row's raw ticker, when the catalogue now has it (the warning was
   * stamped at import time and is never re-evaluated). Absent when there is
   * none or more than one — then picking is the customer's call, not ours.
   */
  catalogMatch?: { assetId: string; symbol: string; name: string }

  /** Origin of the import — `sourceExchange` from the staging row */
  source: string

  /**
   * The ADMIN CATALOGUE name for `source` (`bat.exchanges.name`), resolved
   * server-side through `platformRegistry.display()` — the same source the
   * transactions grid, Traza, Balances and the tax report all show.
   *
   * Without it the tray could only re-spell the slug, so a venue the admin
   * renamed read "Kraken" here and "Kraken Exchange" on every other page.
   *
   * Optional: absent for a slug the catalogue does not know, and absent from an
   * older API response — the client falls back to formatting the slug.
   */
  sourceDisplayName?: string

  /** ISO-8601 timestamp the staging row was processed */
  createdAt: string

  /** Minimal transaction info for the tray list row */
  transactionSummary: AttentionTransactionSummary
}

/**
 * Inline preview of the ledger transaction. Used by the tray list row;
 * the full detail view fetches the transaction separately.
 */
export interface AttentionTransactionSummary {
  /** `trade`, `deposit`, `withdrawal`, etc. — taken from `transactions.attribute` */
  type: string

  /** ISO-8601 timestamp of the transaction itself (not the import) */
  timestamp: string

  /**
   * Headline amount for the row — incoming amount if present, otherwise
   * outgoing, otherwise commission. Stringified at the boundary so JS
   * number precision doesn't truncate small crypto amounts.
   */
  amount: string

  /**
   * Raw ticker that triggered the warning (or the headline asset for
   * non-alias warnings). For `unknown_asset_alias` this is the same as
   * `warningDetails.ticker`.
   */
  asset: string
}

// ============================================================================
// Filters (query string for GET /api/imports/needs-attention)
// ============================================================================

export interface ListNeedsAttentionQuery {
  /** Filter by `imported_transactions.source_exchange` */
  source?: string
  /** Filter by warning type */
  type?: AttentionWarningType
  /** 1-indexed page number (default 1) */
  page?: number
  /** Page size (default 50, max 200) */
  limit?: number
}

export interface ListNeedsAttentionResponse {
  items: AttentionRow[]
  total: number
  page: number
  limit: number

  /**
   * ACCOUNT-WIDE count of unrecognised rows that carry a stored template —
   * i.e. `validation_status='unknown'` (which IS the UNCLASSIFIED set — see
   * {@link UNCLASSIFIED_ROW_WARNING_TYPES}) AND `mapping_id IS NOT NULL`.
   *
   * ⚠️ Deliberately IGNORES `source`/`type` and the page, exactly like the
   * `POST /imports/unknown-rows/reprocess` endpoint it describes: that endpoint
   * takes only `stagingRowIds`/`mappingId`, so the tray-level "Reprocesar"
   * sweeps the whole tenant. Gating the button on the PAGE's rows is what made
   * it vanish on page 2 and under the `unknown_asset_alias` filter — a
   * page-scoped question governing an account-scoped action.
   *
   * Rows with NO template are excluded because the reprocessor reports them
   * `no_mapping_recorded`: there is nothing to re-run for them, so counting
   * them would offer an action that can never move them.
   *
   * ⚠️⚠️ SCOPED TO THE TENANT, NOT THE USER — unlike `items`/`total` above,
   * which are tenant + user. That asymmetry is the REPROCESSOR's
   * (`loadCandidateRows` filters on `tenantId` alone) and is pre-existing; this
   * field deliberately follows the MUTATION so the figure the confirmation
   * prints can never understate what the button touches in a multi-user tenant.
   * Do not "align" it with `total` without first changing the endpoint.
   */
  unrecognisedRowsWithTemplateCount: number

  /**
   * Subset of `unrecognisedRowsWithTemplateCount` whose template classifies
   * ROW BY ROW. The complement is therefore EXACTLY the set whose template
   * needs the whole file — which is what the disabled button's stated reason
   * says, so the sentence cannot drift from the number.
   */
  standaloneReprocessableRowCount: number

  /**
   * De las bloqueadas, las que lo están porque su plantilla AGRUPA: clasificar
   * una fila depende de las demás del fichero.
   *
   * ⚠️⚠️ POR QUÉ ESTÁ PARTIDO EN DOS. Medido en la bandeja el 2026-09-01: 24 de
   * 26 filas bloqueadas lo estaban por `variant-routing` y sólo 2 por
   * `grouping`, pero el agregado sólo llevaba el booleano y la frase del botón
   * deshabilitado afirmaba la agrupación para las 26. El detalle por fila
   * (`warningDetails.fullReimportCause`) ya sabía cuál era; el agregado, no.
   *
   * `standaloneReprocessableRowCount + groupingBlockedRowCount +
   * variantRoutingBlockedRowCount === unrecognisedRowsWithTemplateCount`: es
   * una PARTICIÓN, no tres sondas sueltas.
   */
  groupingBlockedRowCount: number

  /**
   * De las bloqueadas, las que lo están por ENRUTADO DE VARIANTE: la
   * clasificación sí es fila a fila; lo que falta son las cabeceras originales
   * del fichero para elegir la configuración de la plantilla.
   */
  variantRoutingBlockedRowCount: number
}

// ============================================================================
// Resolve actions
// ============================================================================

/**
 * Resolve action discriminator. Two shapes for now; the discriminator is
 * `type` so Zod's discriminatedUnion can do exhaustive narrowing.
 */
export type ResolveAction = ResolveByMappingAsset | ResolveByDiscarding

/**
 * Map a raw ticker to a known asset.
 *
 * Side effects on the backend:
 *   1. Upsert `tenant_asset_aliases (ticker=from, source=row.source, assetId=to)`.
 *   2. Bulk-update all `transactions` rows that share `(tenant, source, ticker)`
 *      to set `*AssetId = to` (incoming / outgoing / commission whichever leg
 *      held the raw ticker).
 *   3. Clear the warning on the staging row (`validationStatus='valid'`).
 *   4. Recalculate price for the now-resolved leg.
 *
 * Returns the count of affected ledger transactions.
 */
export interface ResolveByMappingAsset {
  type: 'map_asset'
  /** Raw ticker that was unknown — e.g. `'BCC'` */
  from: string
  /** Canonical asset ID to map to — e.g. UUID of `Bitcoin Cash` */
  toAssetId: string
}

/**
 * Discard the transaction outright.
 *
 * Side effects on the backend:
 *   1. Soft-delete the `transactions` row (`deletedAt = now()`).
 *   2. Clear `imported_transactions.linked_transaction_id` so the row stops
 *      surfacing in the tray. The staging row itself is preserved for audit.
 */
export interface ResolveByDiscarding {
  type: 'discard'
}

// ============================================================================
// Resolve responses
// ============================================================================

/**
 * Payload under the `{ success, data }` envelope for single resolve/discard
 * (mirrors {@link BulkResolveResult}). The client transport unwraps `data`,
 * so `affected` MUST live under `data`, not at the top level.
 */
export interface ResolveOneResponse {
  /**
   * Number of LEDGER transactions affected. For `discard` this is always 1;
   * for `map_asset` this is the total propagation count (the matched row
   * plus all peers that share the same ticker+source).
   */
  affected: number
}

export interface BulkResolveBody {
  /** Staging-row IDs (= `imported_transactions.id`) */
  txIds: string[]
  resolution: ResolveAction
}

export interface BulkResolveResult {
  /** Number of staging rows successfully resolved */
  resolved: number
  /** Number of staging rows that failed (not found / already resolved / etc.) */
  failed: number
  /**
   * Distinct staging rows actually taken out of the tray — the selection plus
   * the peers a `map_asset` swept. The confirmation promises this wider scope
   * ("se aplicará a TODAS las N … no solo a la fila seleccionada"), so this is
   * the number the success message must report. Optional so an older backend
   * response still parses.
   */
  swept?: number
}
